import React, { useEffect, useState } from 'react';
import useVOPConfirmation from 'components/Payments/VOP/VOPConfirmationModal/useVOPConfirmation';
import VOPConfirmationModal from 'components/Payments/VOP/VOPConfirmationModal/VOPConfirmationModal';
import { Trans } from 'react-i18next';
import { useMutation } from 'react-query';
import { formatNumber } from 'utils/currency.helpers';
import t from 'utils/translationHelper';

import { TradeDto } from '@alpha/fx-dtos';
import { PaymentDto } from '@alpha/payments-dtos';
import { Alert } from '@alpha/ui-lib/ui/Alert/Alert';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { BaseDrawer } from '@alpha/ui-lib/ui/Drawer/APBaseDrawer';
import { StyledDivider } from '@alpha/ui-lib/ui/StyledDivider';
import { StyledKeyValueTable } from '@alpha/ui-lib/ui/table';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { faMoneyBill } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useReportsPolling, {
  DownloadType,
  FileType,
  PageType,
} from '../../../hooks/useReportsPolling';
import { getIndividualBeneficiary } from '../../../services/Beneficiaries/beneficiaries.service';
import FXTradeService from '../../../services/FXTrade/fxTrade.service';
import formatIsoDate from '../../../utils/formatIsoDate';
import mapPaymentStatusDisplay from '../../../utils/payments/mapPaymentStatusDisplay';

import Header from './Header/Index';
import useHeaderStyles from './Header/styles';
import AssociatedTrade from './AssociatedTrade';

interface IPaymentInfoDrawer {
  sharedStyles?: any;
  testIds: {
    paymentDrawerBeneficiaryTable: string;
    paymentDrawerPaymentTable: string;
  };
  paymentDrawerOpen: boolean;
  setPaymentDrawerOpen: Function;
  selectedPayment?: PaymentDto;
}

// eslint-disable-next-line max-lines-per-function
export const PaymentInfoDrawer: React.FC<IPaymentInfoDrawer> = ({
  sharedStyles: classes,
  testIds,
  paymentDrawerOpen,
  setPaymentDrawerOpen,
  selectedPayment,
}: IPaymentInfoDrawer) => {
  const getBeneficiary = useMutation(
    'getBeneficiary',
    getIndividualBeneficiary
  );
  const [paymentTrade, setPaymentTrade] = useState<TradeDto>();
  const [tradeLoading, setTradeLoading] = useState<boolean>(false);
  const [paymentNeedsVOPConfirmation, setPaymentNeedsVOPConfirmation] =
    useState<PaymentDto | undefined>();
  const vopConfirmation = useVOPConfirmation(
    selectedPayment?.vopDetails ? selectedPayment : undefined
  );
  const headerStyles = useHeaderStyles();

  const { executeReportGeneration } = useReportsPolling(PageType.Payments);
  const handleContractDownload = async (tradeId: string) => {
    await executeReportGeneration(
      FileType.PDF,
      tradeId,
      undefined,
      undefined,
      DownloadType.ContractNote
    );
  };

  const loadTradeData = async (queryTradeId: string) => {
    try {
      setTradeLoading(true);
      const tradeData = await FXTradeService.getTradeData(queryTradeId);
      if (tradeData) {
        setPaymentTrade(tradeData);
        setTradeLoading(false);
      }
    } catch (e) {
      setTradeLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPayment?.tradeId) {
      loadTradeData(selectedPayment?.tradeId);
    }
  }, [selectedPayment?.tradeId]);

  useEffect(() => {
    if (selectedPayment?.beneficiaryId) {
      getBeneficiary.mutate(selectedPayment.beneficiaryId);
    }
  }, [selectedPayment?.beneficiaryId]);

  const paymentDrawerPaymentData = [
    {
      key: t('payment_status'),
      value:
        selectedPayment &&
        t(mapPaymentStatusDisplay(selectedPayment.status).text),
      statusValue: true,
      statusVariant:
        selectedPayment &&
        mapPaymentStatusDisplay(selectedPayment?.status).variant,
    },
    {
      key: t('beneficiary_name'),
      value: selectedPayment && selectedPayment.beneficiaryName,
      boldValue: true,
    },
    {
      key: t('beneficiary_bank'),
      value: getBeneficiary.data && (getBeneficiary.data.bankName || '-'),
    },
    {
      key: t('swift/bic'),
      value: getBeneficiary?.data ? getBeneficiary.data.swift || '-' : '-',
      datadogMask: true,
    },
    {
      key: t('iban'),
      value: getBeneficiary?.data ? getBeneficiary.data.iban || '-' : '-',
      datadogMask: true,
    },
    {
      key: t('national_bank_code'),
      value: getBeneficiary?.data
        ? getBeneficiary.data.nationalBankCode || '-'
        : '-',
    },
    {
      key: t('account_number'),
      value: getBeneficiary?.data
        ? getBeneficiary.data.accountNumber || '-'
        : '-',
        datadogMask: true,
    },
    {
      key: t('payment_date'),
      value: selectedPayment && formatIsoDate(selectedPayment.valueDate),
    },
    {
      key: t('payment_reference'),
      value: selectedPayment && selectedPayment.reference,
    },
    {
      key: t('payment_amount'),
      value:
        selectedPayment &&
        `${selectedPayment.debitingCurrency} ${formatNumber(
          selectedPayment.amount,
          2
        )}`,
      boldKey: false,
      boldValue: true,
    },
  ];

  return (
    <>
      <BaseDrawer.Drawer
        open={paymentDrawerOpen}
        onClose={() => {
          setPaymentDrawerOpen(false);
        }}
        anchor='right'
        closeIdentifier='payment-info-drawer'
      >
        <Header
          selectedPayment={selectedPayment}
          viewOnly={false}
          icon={
            <FontAwesomeIcon
              icon={faMoneyBill}
              color='rgba(30, 135, 119, 1)'
              size='xl'
              style={{ marginRight: '8px' }}
            />
          }
        />

        {selectedPayment && (
          <div style={{ marginTop: '8px' }}>
            <BaseDrawer.Body>
              <>
                <div className={headerStyles.sectionBox}>
                  <h4
                    style={{
                      marginTop: '0px',
                      fontWeight: '600',
                      fontSize: '16px',
                    }}
                  >
                    {t('payment_details')}
                  </h4>
                  <StyledKeyValueTable
                    testId={testIds.paymentDrawerPaymentTable}
                    data={paymentDrawerPaymentData}
                  />
                </div>
                {selectedPayment.tradeId && (
                  <div
                    style={{ marginTop: '8px' }}
                    className={headerStyles.sectionBox}
                  >
                    <h4
                      style={{
                        marginTop: '0px',
                        fontWeight: '600',
                        fontSize: '16px',
                      }}
                    >
                      {t('associated_trade')}
                    </h4>
                    <AssociatedTrade
                      loading={tradeLoading}
                      trade={paymentTrade}
                      handlePaymentTradeContractNoteDownload={
                        handleContractDownload
                      }
                    />
                  </div>
                )}

                {selectedPayment?.vopRequired && (
                  <div
                    style={{ marginTop: '8px' }}
                    className={headerStyles.sectionBox}
                  >
                    <Typography
                      variant='h3'
                      style={{
                        marginTop: '0px',
                        marginBottom: '16px',
                        fontWeight: '600',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={vopConfirmation.icon}
                        color={vopConfirmation.color}
                        size='sm'
                        style={{ marginRight: '8px' }}
                      />
                      {vopConfirmation.title}
                    </Typography>
                    <Typography
                      variant='body1'
                      style={{ marginBottom: '24px' }}
                    >
                      <Trans
                        components={{ strong: <strong /> }}
                        values={{
                          name: selectedPayment?.beneficiaryName ?? '',
                          correct_name: selectedPayment?.vopDetails?.name ?? '',
                        }}
                      >
                        {vopConfirmation.message}
                      </Trans>
                    </Typography>

                    <StyledDivider />

                    {vopConfirmation.closeMatchTable.length > 0 && (
                      <>
                        <StyledKeyValueTable
                          testId='close-match-table'
                          data={vopConfirmation.closeMatchTable}
                        />
                      </>
                    )}

                    <StyledKeyValueTable
                      testId='matching-table'
                      data={vopConfirmation.matchedTable}
                    />

                    {selectedPayment?.vopDetails?.vopApprovalAcknowledged && (
                      <div style={{ marginTop: '20px' }}>
                        <Alert
                          variant='info'
                          text={
                            <Typography>
                              {<strong>{t('please_note')}</strong>}{' '}
                              {t('cop_alert_message_acknowledged')}{' '}
                              {selectedPayment?.vopDetails?.vopApproverName ||
                                'Unknown'}{' '}
                              {t('on')}{' '}
                              {selectedPayment?.vopDetails?.vopApprovalDateTime
                                ? formatIsoDate(
                                    selectedPayment?.vopDetails
                                      ?.vopApprovalDateTime
                                  )
                                : '-'}
                              .
                            </Typography>
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '24px', padding: '0 20px 40px 20px' }}>
                  <ActionButton
                    onClick={() => setPaymentDrawerOpen(false)}
                    className='cancelButton'
                    fullWidth
                    style={{ background: '#F7F7F7', color: '#212529' }}
                  >
                    {t('cancel')}
                  </ActionButton>
                </div>

                <VOPConfirmationModal
                  readOnly={true}
                  open={!!paymentNeedsVOPConfirmation}
                  handleClose={() => setPaymentNeedsVOPConfirmation(undefined)}
                  payment={paymentNeedsVOPConfirmation}
                />
              </>
            </BaseDrawer.Body>
          </div>
        )}
      </BaseDrawer.Drawer>
    </>
  );
};

export default PaymentInfoDrawer;
